// Sugalarm — Cycle 236: reminder fullscreen-overlay state.
// Cycle 272: cross-device done fan-out.
// Cycle 359: reminder Snooze REMOVED — a fired reminder is Close-only.
// Cycle 361: a fired reminder AUTO-DISMISSES after REMINDER_AUTO_DISMISS_MS.
//
// Owns the single "currently displayed reminder". The full-screen
// <ReminderOverlay> is the sole display surface for a fired reminder; a single
// Close dismisses it. Pairs with useTTS.speakReminder for the spoken
// announcement of a fresh fire.
//
// Cycle 359 — why the snooze machinery is gone: the client-side 5-minute
// snooze re-show timer (and the cross-device `reminder_snoozed` frame that
// re-armed it on every signed-in device) was ONE cause of scheduled reminders
// "popping up randomly" — a forgotten/double-tapped Snooze, or a snooze echoed
// to a device the tester forgot was signed in, made a reminder silently
// reappear ~5 minutes after it was dismissed.
// (The server `POST /api/reminders/{id}/snooze` endpoint + `reminder_snoozed`
// frame remain in place but DORMANT — the shipped clients no longer invoke or
// act on them; see reminder_routes.py / useGlucoseSocket.ts.)
//
// Cycle 361 — the SECOND cause, and why the deadline lives HERE and not in the
// component. Cycle 359 was incomplete: `activeReminder` had no expiry, so an
// un-Closed reminder parked in client state forever, and App.tsx's
// alarm-yield gate hid it while an alarm was live and re-showed it whenever
// the alarm cleared. Measured on a real account, an alarm was live at the
// reminder's 10:00 AM slot on 18 of 21 days (median 5.3 h), so a 10:00
// reminder routinely resurfaced mid-afternoon looking like a random pop-up.
// A fired reminder now carries the wall-clock instant it arrived and is
// dropped once that instant is REMINDER_AUTO_DISMISS_MS old.
//
// TWO PROPERTIES THIS DEADLINE MUST KEEP — do not "simplify" either away:
//
//  (1) It is a WALL-CLOCK DEADLINE compared against `Date.now()`, never a
//      `setTimeout(…, 10 min)`. Webviews suspend JS timers while backgrounded
//      and fire them arbitrarily late on resume — the exact mechanism behind
//      the cycle-351 app-open false-high and the reason cycle-351 rebuilt
//      alarm-preview expiry as a read-time deadline. A suspendable timer here
//      would recreate the very "pops up hours later" bug this removes.
//
//  (2) It runs REGARDLESS OF VISIBILITY. The expiry lives in this hook, not in
//      <ReminderOverlay>, so a reminder withheld behind a sounding alarm still
//      ages out on schedule and can NEVER resurface later. Moving the timer
//      into the component would restore the resurface.
//
// Behaviour contract (see App.tsx wiring):
//  - setActiveReminder(r): show r now, stamping its arrival instant.
//  - dismiss(): hide (the Close path). Cross-device: App.tsx also POSTs the
//    cycle-272 dismiss so Close on one device clears the overlay on all.
//  - applyRemoteDismiss(id): apply a cross-device dismiss ONLY when it targets
//    the reminder this device is currently showing (id match) — idempotent on
//    the originator.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  isReminderExpired,
  REMINDER_AUTO_DISMISS_MS,
} from "../lib/reminderExpiry";
import type { Reminder, ReminderFire } from "../lib/types";

export { REMINDER_AUTO_DISMISS_MS };

/** How often the deadline is re-checked while a reminder is outstanding. The
 *  interval is only a nudge to re-render — correctness comes from the
 *  `Date.now()` comparison, so a suspended/throttled tick can only ever make
 *  the dismissal late-by-one-tick, never wrong. */
const EXPIRY_TICK_MS = 1_000;

interface PendingReminder {
  reminder: Reminder;
  /** Cycle 362: the durable server-side occurrence, when the server sent one
   *  (null on a `/test` fire and from a pre-362 server). Its presence is what
   *  lets Close acknowledge THIS fire durably instead of only broadcasting. */
  fire: ReminderFire | null;
  /** Wall-clock ms when this fire arrived on THIS device. */
  firedAtMs: number;
}

export interface ReminderOverlayState {
  /** The reminder currently displayed (null = overlay hidden). */
  activeReminder: Reminder | null;
  /** Cycle 362: the displayed reminder's durable occurrence, if any. Drives
   *  the durable Close and the "scheduled for" label. */
  activeFire: ReminderFire | null;
  /** Show a reminder now. */
  setActiveReminder: (reminder: Reminder, fire?: ReminderFire | null) => void;
  /** Hide the overlay (Close / Done). */
  dismiss: () => void;
  /** Cycle 272: a dismiss (Close) that happened on another of the owner's
   *  devices. Clears the overlay here when it targets the shown reminder —
   *  matched on the cycle-362 occurrence id when present (precise), else on
   *  the reminder id (the pre-362 fallback). */
  applyRemoteDismiss: (reminderId: string, fireId?: string | null) => void;
}

export function useReminderOverlay(): ReminderOverlayState {
  const [pending, setPending] = useState<PendingReminder | null>(null);
  // Ref mirror so the imperative methods stay stable (empty-dep useCallbacks),
  // and can therefore sit in the useGlucoseSocket callbacks memo without
  // churning it every time a reminder shows/hides.
  const pendingRef = useRef<PendingReminder | null>(null);

  const commitPending = useCallback((next: PendingReminder | null) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const setActiveReminder = useCallback(
    (reminder: Reminder, fire: ReminderFire | null = null) => {
      commitPending({ reminder, fire, firedAtMs: Date.now() });
    },
    [commitPending],
  );

  const dismiss = useCallback(() => {
    commitPending(null);
  }, [commitPending]);

  const applyRemoteDismiss = useCallback(
    (reminderId: string, fireId?: string | null) => {
      const current = pendingRef.current;
      if (current == null) return;
      const matches =
        fireId != null && current.fire != null
          ? current.fire.id === fireId
          : current.reminder.id === reminderId;
      if (matches) dismiss();
    },
    [dismiss],
  );

  // Cycle 361: the auto-dismiss deadline. Ticks ONLY while a reminder is
  // outstanding (no idle work), re-checks immediately on mount and on
  // foreground so a webview that slept through the deadline clears at once
  // rather than on the next tick.
  useEffect(() => {
    if (pending == null) return;

    const check = () => {
      if (isReminderExpired(pending.firedAtMs, Date.now())) {
        commitPending(null);
      }
    };

    check();
    const id = window.setInterval(check, EXPIRY_TICK_MS);

    let onVis: (() => void) | null = null;
    if (typeof document !== "undefined") {
      onVis = () => {
        if (document.visibilityState === "visible") check();
      };
      document.addEventListener("visibilitychange", onVis);
    }

    return () => {
      window.clearInterval(id);
      if (onVis != null && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }, [pending, commitPending]);

  return {
    activeReminder: pending?.reminder ?? null,
    activeFire: pending?.fire ?? null,
    setActiveReminder,
    dismiss,
    applyRemoteDismiss,
  };
}
